import { useState, useCallback } from 'react';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';
import * as arbitraryEvents from 'js/core_webpack/arbitrary_events';
import { WorkData } from '../types';
import { prepareWorksPayload, downloadFile } from '../utils/exportCitations';
import { addReferencesToDoc, getDocumentWithReferencesStatus } from '../api';

type Citation = {
  name: string;
  citation: string;
};

export const useCiterightCitations = () => {
  const [citations, setCitations] = useState<Citation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const generateCitationsForSavedReferences = useCallback(
    async (savedReferences: WorkData[]): Promise<Citation[]> => {
      if (!savedReferences.length) {
        return [];
      }

      setIsLoading(true);
      setError(null);

      try {
        // Build batch payload with all works
        const works = prepareWorksPayload(savedReferences);

        const response = await fetchWithCsrf(
          routes.api_v0_citeright_generate_citations_path({
            subdomain_param: 'api',
          }),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ works }),
          },
        );

        if (!response.ok) {
          throw new Error(
            `Failed to generate citations: ${response.statusText}`,
          );
        }

        const data = await response.json();
        const allCitations = data.citations || [];

        setCitations(allCitations);
        return allCitations;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to generate citations';
        setError(errorMessage);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const generateBibtexForSavedReferences = useCallback(
    async (savedReferences: WorkData[]): Promise<void> => {
      if (!savedReferences.length) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Build batch payload with all works
        const works = prepareWorksPayload(savedReferences);

        const response = await fetchWithCsrf(
          routes.api_v0_citeright_export_bibtex_path({
            subdomain_param: 'api',
          }),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ works }),
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to export BibTeX: ${response.statusText}`);
        }

        // Handle file download
        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        const defaultFilename = `references_${new Date()
          .toISOString()
          .slice(0, 16)
          .replace(/[-:T]/g, '')}.bib`;

        downloadFile(blob, defaultFilename, contentDisposition);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to export BibTeX';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const generateRisForSavedReferences = useCallback(
    async (savedReferences: WorkData[]): Promise<void> => {
      if (!savedReferences.length) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Build batch payload with all works
        const works = prepareWorksPayload(savedReferences);

        const response = await fetchWithCsrf(
          routes.api_v0_citeright_export_ris_path({
            subdomain_param: 'api',
          }),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ works }),
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to export RIS: ${response.statusText}`);
        }

        // Handle file download
        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        const defaultFilename = `references_${new Date()
          .toISOString()
          .slice(0, 16)
          .replace(/[-:T]/g, '')}.ris`;

        downloadFile(blob, defaultFilename, contentDisposition);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to export RIS';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const generateModifiedDocxForSavedReferences = useCallback(
    async (savedReferences: WorkData[], reportId: number): Promise<void> => {
      if (!savedReferences.length) {
        return;
      }

      if (!reportId) {
        setError('Report ID is required');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Build batch payload with all works
        const works = prepareWorksPayload(savedReferences);

        // Call API to start generation
        await addReferencesToDoc(reportId, works);

        // Poll for document completion
        const pollInterval = 2000; // 2 seconds
        const maxPollAttempts = 150; // 5 minutes max (150 * 2s)
        let pollAttempts = 0;

        const pollForDocument = async (): Promise<void> => {
          while (pollAttempts < maxPollAttempts) {
            try {
              const status = await getDocumentWithReferencesStatus(reportId);

              if (status.ready && status.url) {
                // Document is ready, download it
                const response = await fetchWithCsrf(status.url);
                if (!response.ok) {
                  throw new Error('Failed to download document');
                }

                const blob = await response.blob();
                const defaultFilename = `document_with_references_${new Date()
                  .toISOString()
                  .slice(0, 16)
                  .replace(/[-:T]/g, '')}.docx`;

                downloadFile(blob, status.filename || defaultFilename);
                arbitraryEvents.write(
                  'CiterightDownloadReferencesDocxSuccess',
                  {
                    report_id: reportId,
                    reference_count: savedReferences.length,
                  },
                );

                return;
              }

              // Wait before next poll
              await new Promise((resolve) => setTimeout(resolve, pollInterval));
              pollAttempts++;
            } catch (err) {
              // Continue polling on error (might be temporary)
              if (pollAttempts >= maxPollAttempts - 1) {
                throw err;
              }
              await new Promise((resolve) => setTimeout(resolve, pollInterval));
              pollAttempts++;
            }
          }

          // Timeout reached
          throw new Error('Document generation timed out. Please try again.');
        };

        await pollForDocument();
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to add references to document';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return {
    generateCitationsForSavedReferences,
    generateBibtexForSavedReferences,
    generateRisForSavedReferences,
    generateModifiedDocxForSavedReferences,
    citations,
    isLoading,
    error,
    clearError,
  };
};
